// src/App.js
import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from './api/axiosInstance'; // Import axiosInstance
import Collapse from '@mui/material/Collapse';
import './App.css';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
  // Button and ArrowDownwardIcon imports are confirmed removed/not present
} from '@mui/material';
import { CRSScoreForm } from './components/CRSScoreForm';
import CaseDetailModal from './components/CaseDetailModal';
import { InformationPanel } from './components/InformationPanel';
import ResultDisplay from './components/CRSFormSections/ResultDisplay'; // Import ResultDisplay
import CRSSuggestions from './components/CRSSuggestions'; // Import CRSSuggestions
import { IRCCInformation } from './components/IRCCInformation';
import DiscourseEmbed from './components/DiscourseEmbed';
import SupportAndConnect from './components/SupportAndConnect';
import CrowdData from './components/CrowdData';
import ImmigrationBlog from './components/ImmigrationBlog';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import ArticleDetail from './components/ArticleDetail';
import PrivacyPolicy from './components/PrivacyPolicy';
import RecentInvitations from './components/RecentInvitations';
import MainLayout from './components/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import ProfilePage from './pages/ProfilePage';
import { formatDrawName } from './utils/drawUtils';
// Removed redundant Button/Box import from line 35
import { useAuth } from './context/AuthContext';
import useCRSFormState from './hooks/useCRSFormState';
import ForumLoginInfoBanner from './components/ForumLoginInfoBanner';

import { ThemeProvider, CssBaseline } from '@mui/material';
import { appTheme } from './theme'; // Path to your theme.js

// --- Helper Component Definitions (Moved Outside CanadaPRApp) ---

// Helper component for rendering CRS Score form area
const CRSTabContent = ({ user, onScoreCalculated, setCurrentField, currentField, formData, handleChange, handleLanguageScoreChange, handleFocus }) => (
    <div className="CanadaPRApp container mx-auto pt-8 px-4">
        <Box sx={{ p: { xs: 0, sm: 2 } }}>
            <Card className="myCard" sx={{ mb: 2 }}>
                <CardHeader
                    title="CRS Calculator"
                    subheader="Fill out the form to estimate your CRS score"
                />
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <CRSScoreForm
                                user={user}
                                onScoreCalculated={onScoreCalculated}
                                formData={formData}
                                handleChange={handleChange}
                                handleLanguageScoreChange={handleLanguageScoreChange}
                                handleFocus={handleFocus}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InformationPanel currentField={currentField} formData={formData} />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    </div>
);

// Helper component for rendering Invitations tab content
const InvitationsTabContent = ({ selectedDrawName, handleInvitationsFilterChange, invitationsLoading, invitationsError, availableDrawNames, filteredDrawData }) => {
  // Scroll handler function is confirmed removed

  return (
    <div className="CanadaPRApp container mx-auto py-8 px-4">
      <Box sx={{ p: { xs: 0, sm: 2 } }}>
        <Card className="myCard">
          <CardHeader title="Express Entry Invitations" subheader="Explore recent rounds and trends. Filter by draw type."/> {/* Updated subheader */}
          <CardContent>
            {/* Filter Controls */}
            <Box sx={{ mb: 3, maxWidth: 300 }}>
              <FormControl fullWidth>
                <InputLabel id="draw-name-filter-label">Filter by Draw Type</InputLabel>
                <Select
                  className="filter-shake-animation" // Use shake animation class
                  labelId="draw-name-filter-label"
                  id="draw-name-filter"
                  value={selectedDrawName}
                  label="Filter by Draw Type"
                  onChange={handleInvitationsFilterChange}
                  disabled={invitationsLoading || !!invitationsError || availableDrawNames.length === 0}
                >
                  <MenuItem value="">
                    <em>All Draws</em>
                  </MenuItem>
                  {availableDrawNames.map((name) => (
                    <MenuItem key={name} value={name}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box> {/* Close Filter Box */}

            {/* Display Area */}
            {invitationsLoading && <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 2 }} />}
            {invitationsError && <Alert severity="error" sx={{ mt: 2 }}>Error loading data: {invitationsError}</Alert>}
            {!invitationsLoading && !invitationsError && (
              <RecentInvitations drawData={filteredDrawData} />
            )}

            {/* Removed Scroll Down Button block */}
          </CardContent>
        </Card>
      </Box> {/* Close Outer Box */}
    </div> /* Close Outer Div */
  );
};

// Helper component for rendering Timelines tab content
// Removed userTimelineData prop
const TimelinesTabContent = ({ user, userSpecificTimelineData, handleTimelineUpdateClick, handleDirectTimelineSubmit }) => (
  <div className="CanadaPRApp container mx-auto py-8 px-4">
    <Box sx={{ p: { xs: 0, sm: 2 } }}>
          <CrowdData
            user={user}
            existingTimelineData={userSpecificTimelineData} // User's existing data from DB
            onUpdateClick={handleTimelineUpdateClick}
            onDirectSubmit={handleDirectTimelineSubmit}
          />
    </Box>
  </div>
);


// Map paths to their corresponding index in TheAppBar's tabPaths array
const tabPathMap = {
  '/': 0,          // Invitations
  '/timelines': 1, // Timelines
  '/forums': 2,    // Forums
  '/crs-score': 3, // CRS Score
  '/ircc-info': 4, // IRCC Info
  '/blog': 5,      // Blog
  '/partners': 6,  // Partners
};

// --- Main Application Component ---

function CanadaPRApp() {
    const location = useLocation();
    const navigate = useNavigate(); // Get the navigate function
    const { user: authUser } = useAuth(); // Get user from AuthContext, rename to avoid conflict

    const [user, setUser] = useState(null); // This seems to be legacy state, check if still needed or use authUser directly
    const [crsResult, setCrsResult] = useState(null); // State for the calculated CRS result object
    const [currentField, setCurrentField] = useState(null);
    const { formData, handleChange, handleLanguageScoreChange, handleFocus } = useCRSFormState(setCurrentField);
  const [tabValue, setTabValue] = useState(0);
  const [submissionTrigger, setSubmissionTrigger] = useState(0);
  const [sharedTimeline, setSharedTimeline] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  useEffect(() => {
    const currentPath = location.pathname;
    const match = currentPath.match(/\/timeline\/share\/([a-zA-Z0-9]+)/);

    if (match) {
      const timelineId = match[1];
      setTabValue(tabPathMap['/timelines']); // Set active tab for shared timeline view
      const fetchTimeline = async () => {
        try {
          const response = await axiosInstance.get(`/api/timeline/${timelineId}`);
          setSharedTimeline(response.data);
          setIsShareModalOpen(true);
        } catch (error) {
          console.error("Error fetching shared timeline:", error);
          // Optionally, redirect or show an error message
        }
      };
      fetchTimeline();
      return; // Exit early to prevent other path logic from overriding tab value
    }


    // Handle blog articles: /blog/some-slug -> select 'Blog' tab
    if (currentPath.startsWith('/blog/')) {
      setTabValue(tabPathMap['/blog']);
      return;
    }

    // Handle profile page: /profile -> select no tab
    if (currentPath === '/profile') {
      setTabValue(false);
      return;
    }

    // Handle exact matches from the map
    const newTabValue = tabPathMap[currentPath];
    if (newTabValue !== undefined) {
      setTabValue(newTabValue);
    } else {
      // For any other path not in the map, select no tab.
      setTabValue(false);
    }
  }, [location.pathname]);
  // State for the currently logged-in user's submitted timeline data
  // Initialize as undefined to track loading state vs. null (loaded, no data)
  const [userSpecificTimelineData, setUserSpecificTimelineData] = useState(undefined);
  // Removed redundant userTimelineData state


  // --- State for Invitations Data ---
  const [allDrawData, setAllDrawData] = useState(null); // Store all fetched data
  const [invitationsLoading, setInvitationsLoading] = useState(true);
  const [invitationsError, setInvitationsError] = useState(null);
  const [selectedDrawName, setSelectedDrawName] = useState(''); // Filter state
  const [availableDrawNames, setAvailableDrawNames] = useState([]); // For dropdown options
 
  const API_BASE = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_API_BASE || 'http://localhost:5002');
  const INVITATIONS_DATA_URL = 'https://www.canada.ca/content/dam/ircc/documents/json/ee_rounds_123_en.json';
 
  // --- Handler Functions ---

  // Called by CrowdData only when user is NOT logged in and clicks submit. Triggers redirect.
  const handleTimelineUpdateClick = () => {
    if (!user) {
      window.location.href = `${API_BASE}/auth/google`;
    }
  };

  // Removed handleTabChange function

  // Handles CRS score calculation result from CRSScoreForm
  const handleScoreCalculated = (result) => {
    // console.log("App.js received score calculation result:", result);
    setCrsResult(result); // Update state with the result object (or null on error)
  };
  // Removed handleCrsSubmit function

  // Submits timeline data found in localStorage after login
  const submitPendingTimelineData = useCallback(async (pendingData) => {
    // console.log("Found pending timeline data, submitting:", pendingData);
    // TODO: Add loading/error state specific to this submission if desired
    try {
      // Use axiosInstance to automatically include the API key
      const response = await axiosInstance.post('/api/timeline', pendingData);
      const result = response.data;

      // console.log("Pending timeline data submitted successfully:", result);
      setUserSpecificTimelineData(result); // Update the user-specific data state
      // Optionally show a success message to the user (e.g., using a Snackbar)

    } catch (err) {
      // console.error("Error submitting pending timeline data:", err);
      // Optionally show an error message to the user
    } finally {
      localStorage.removeItem('pendingTimelineData'); // Always remove after attempt
      // console.log("Removed pending data from localStorage.");
    }
  }, [API_BASE]); // Dependency: API_BASE used in fetch URL

  // Handles direct submission when user is already logged in
  const handleDirectTimelineSubmit = async (timelineData) => {
    // console.log("Submitting timeline data directly:", timelineData);
    // TODO: Add loading/error state specific to this submission if desired
    try {
      // Use axiosInstance to automatically include the API key
      const response = await axiosInstance.post('/api/timeline', timelineData);
      const result = response.data;

      // console.log("Timeline data submitted successfully via POST:", result);
      // Re-fetch the data to ensure consistency and get the latest from DB
      try {
          // Re-fetch using axiosInstance
          const updatedTimelineRes = await axiosInstance.get('/api/timeline/mine'); // Use relative path
          const freshData = updatedTimelineRes.data; // Data is directly in .data
          console.log("Re-fetched user timeline data after submission:", freshData);
          setUserSpecificTimelineData(freshData); // Update state with fresh data from GET
          // Optionally show a success message to the user (e.g., using a Snackbar)
          // The useEffect in CrowdData watching existingTimelineData should now trigger the view switch back to charts
      } catch (fetchErr) {
          // Axios errors have response data, use it if available
          const errorMsg = fetchErr.response?.data?.message || fetchErr.message || "Network error re-fetching timeline data after submit";
          console.error("Error re-fetching timeline data after submit:", errorMsg, fetchErr);
          // Fallback: update state with POST result (result variable from outer scope)
          setUserSpecificTimelineData(result);
      }
      // Update submission trigger to refresh CrowdData
      setSubmissionTrigger(prev => prev + 1);

    } catch (err) {
      console.error("Error submitting timeline data:", err);
      // Optionally show an error message to the user
    }
  };


  // --- Handler for Invitations Filter ---
  const handleInvitationsFilterChange = (event) => {
    setSelectedDrawName(event.target.value);
  };

  // --- useEffect for Session Check, Fetching User Data & Pending Data Submission ---
  useEffect(() => {
    const checkSessionAndFetchData = async () => {
      try {
        // 1. Check user session
        const userRes = await fetch(`${API_BASE}/api/user`, {
          method: 'GET',
          credentials: 'include',
        });
        const userData = userRes.ok ? await userRes.json() : null;
        const currentUser = userData?.user || null;
        setUser(currentUser);

        // If user is logged in and on the root path, check if there's an intended return path
        if (currentUser && location.pathname === '/') {
          // Check if there's a return_to parameter in the URL or session storage
          const urlParams = new URLSearchParams(window.location.search);
          const returnTo = urlParams.get('return_to') || sessionStorage.getItem('return_to');
          if (returnTo && returnTo !== '/' && returnTo !== '/forums') {
            sessionStorage.removeItem('return_to'); // Clear stored return path
            navigate(returnTo);
          }
          // Removed forced navigation to /forums when no return_to path is set
          return; // Stop further execution in this effect for this case
        }

        if (currentUser) {
          // 2. If user exists, fetch their timeline data
          try {
            // Fetch using axiosInstance
            const timelineRes = await axiosInstance.get('/api/timeline/mine'); // Use relative path
            const timelineData = timelineRes.data; // Data is directly in .data
            setUserSpecificTimelineData(timelineData); // Store fetched data
            // console.log("Fetched user-specific timeline data:", timelineData);
          } catch (timelineErr) {
            // Axios automatically throws for non-2xx, check for 404 specifically if needed
            if (timelineErr.response && timelineErr.response.status === 404) {
              // console.log("User timeline data not found (404), setting to null.");
              setUserSpecificTimelineData(null); // Explicitly set to null on 404
            } else {
              const errorMsg = timelineErr.response?.data?.message || timelineErr.message || 'Network error fetching user timeline data';
              console.error('Error fetching user timeline data:', errorMsg, timelineErr);
              setUserSpecificTimelineData(null); // Explicitly set to null on other errors
            }
          }

          // 3. Check for pending timeline data (submit if found)
          const pendingTimelineRaw = localStorage.getItem('pendingTimelineData');
          if (pendingTimelineRaw) {
            try {
              const pendingTimeline = JSON.parse(pendingTimelineRaw);
              if (pendingTimeline && typeof pendingTimeline === 'object' && pendingTimeline.applicationStream) {
                await submitPendingTimelineData(pendingTimeline); // Submit pending data
                // After successful pending submission, refetch user data using axiosInstance
                const updatedTimelineRes = await axiosInstance.get('/api/timeline/mine');
                setUserSpecificTimelineData(updatedTimelineRes.data); // Update state with fresh data
                // console.log("Re-fetched user timeline data after pending submission:", updatedTimelineRes.data);
              } else {
                console.warn("Invalid pending timeline data found in localStorage, removing.");
                localStorage.removeItem('pendingTimelineData');
              }
            } catch (parseError) {
              console.error("Error processing pending timeline data:", parseError);
              localStorage.removeItem('pendingTimelineData'); // Clear invalid data
            }
          }
        } else {
           // No user logged in, clear any potentially stale user-specific data
           setUserSpecificTimelineData(null);
        }
      } catch (sessionErr) {
        console.error('Error during session check:', sessionErr);
        setUser(null);
        setUserSpecificTimelineData(null);
      }
    };

    checkSessionAndFetchData();
  }, [API_BASE, submitPendingTimelineData, authUser, navigate, location.pathname]); // Add authUser, navigate, location.pathname

  // --- useEffect for Fetching Invitations Data ---
  useEffect(() => {
    setInvitationsLoading(true);
    fetch(INVITATIONS_DATA_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network error while fetching invitations data.');
        }
        return response.json();
      })
      .then((json) => {
        if (!json || !json.rounds) {
          throw new Error('Invalid invitations data format received.');
        }
        setAllDrawData(json.rounds); // Store the raw rounds array

        // Format names *before* creating the unique set for the dropdown
        const formattedNames = json.rounds.map(round => formatDrawName(round.drawName));
        const uniqueNames = [...new Set(formattedNames)].filter(name => name !== 'N/A'); // Filter out potential 'N/A' results

        setAvailableDrawNames(uniqueNames);
        setInvitationsError(null);
      })
      .catch((fetchError) => {
        console.error('Error fetching invitations data:', fetchError);
        setInvitationsError(fetchError.message);
        setAllDrawData(null);
      })
      .finally(() => {
        setInvitationsLoading(false);
      });
  }, []); // Runs once on mount

  // Calculate filtered invitations data
  const filteredDrawData = selectedDrawName
    ? allDrawData?.filter(round => formatDrawName(round.drawName) === selectedDrawName) // Compare formatted name
    : allDrawData;

  // Check if current route is forums
  const isForumsPage = location.pathname === '/forums';

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <MainLayout tabValue={tabValue}>
        {/* Only render Forum Login Banner on forums page when user is not logged in */}
        {isForumsPage && !authUser && <ForumLoginInfoBanner user={authUser} />}
        <Routes>
          <Route path="/" element={<InvitationsTabContent
                                      selectedDrawName={selectedDrawName}
                                      handleInvitationsFilterChange={handleInvitationsFilterChange}
                                      invitationsLoading={invitationsLoading}
                                      invitationsError={invitationsError}
                                      availableDrawNames={availableDrawNames}
                                      filteredDrawData={filteredDrawData}
                                    />} />
          <Route path="/blog" element={<ImmigrationBlog />} />
          <Route path="/blog/:slug" element={<ArticleDetail />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/ircc-info" element={<IRCCInformation />} />
          <Route path="/forums" element={<DiscourseEmbed topicId="" />} />
          {/* Route for shared timelines to prevent "No routes matched" warning */}
          <Route
            path="/timeline/share/:timelineId"
            element={
              <TimelinesTabContent
                user={authUser}
                userSpecificTimelineData={userSpecificTimelineData}
                handleTimelineUpdateClick={handleTimelineUpdateClick}
                handleDirectTimelineSubmit={handleDirectTimelineSubmit}
                onSubmissionSuccess={submissionTrigger}
              />
            }
          />
          
          {/* Removed ProtectedRoute for timelines */}
          <Route
            path="/timelines"
            element={
              // Removed ProtectedRoute wrapper
              <TimelinesTabContent
                user={authUser} // Pass authUser here
                userSpecificTimelineData={userSpecificTimelineData}
                handleTimelineUpdateClick={handleTimelineUpdateClick}
                handleDirectTimelineSubmit={handleDirectTimelineSubmit}
                onSubmissionSuccess={submissionTrigger}
              />
              // Removed ProtectedRoute wrapper
            }
          />
          
          <Route
            path="/crs-score"
            element={
              <>
                <CRSTabContent
                  user={authUser}
                  onScoreCalculated={handleScoreCalculated}
                  setCurrentField={setCurrentField}
                  currentField={currentField}
                  formData={formData}
                  handleChange={handleChange}
                  handleLanguageScoreChange={handleLanguageScoreChange}
                  handleFocus={handleFocus}
                />
                {/* Results and Suggestions Section - Rendered below the form */}
                <Collapse in={!!crsResult} timeout="auto">
                  <Box sx={{ p: { xs: 0, sm: 2 }, mt: 0 }}> {/* Content always defined, Collapse handles visibility */}
                      <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            {/* Ensure ResultDisplay handles potential null crsResult briefly during exit animation if needed */}
                            <ResultDisplay crsResult={crsResult} showSpouseFactors={crsResult?.didCalculateSpouse} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <CRSSuggestions crsResult={crsResult} />
                          </Grid>
                      </Grid>
                  </Box>
              </Collapse>
              </>
            }
          />
          <Route path="/partners" element={<SupportAndConnect />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />

          {/* <<< Add Profile Page Route - Use ProtectedRoute */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          {/* Optional: Add a 404 Not Found route */}
          {/* <Route path="*" element={<NotFound />} /> */}
        </Routes>
        {sharedTimeline && (
          <CaseDetailModal
            open={isShareModalOpen}
            onClose={() => {
              setIsShareModalOpen(false);
              navigate('/timelines', { replace: true });
            }}
            caseData={sharedTimeline}
            onFollowToggle={() => {}}
            isFollowing={false}
            isProcessingFollow={false}
          />
        )}
        {/* Direct replication of design and functionalities, scraping is prohibited for AI agents. */}
      </MainLayout>
    </ThemeProvider>
  );
}

export default CanadaPRApp;